import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import {Client, GraphError,} from '@microsoft/microsoft-graph-client';
import {MicrosoftLogin} from './MicrosoftLogin';
import {ExtFilter, IFilePickerOptions, SPItem} from "./sdk-pnptimeline/types";
import {MSALAuthenticate} from "./sdk-pnptimeline/behaviors/msal-authenticate";
import {ResolveWithPicks} from "./sdk-pnptimeline/behaviors/resolves";
import {Popup} from "./sdk-pnptimeline/behaviors/popup-behaviors";
import {Picker} from "./sdk-pnptimeline/Picker";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {Injectable} from "@angular/core";
import {Skyfil, ValgteSkyfiler} from "web-ng2/delt/komponenter/filvelger-skytjenester/SkyfilOpplasting";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {MicrosoftOneDrivePickerKonfigurasjonModell, SakSkyfilOpplastingRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {tillatteFiltyper} from "felles/tjenester/Filer";

const debug = initDebugger('OneDriveFilvelgerGui');


/**
 * Tjeneste for å vise en filvelger som lar bruker velge fil fra onedrive sharepoint (onedrive for business).
 * <pre>
 * Merk at vi i denne implementasjonen ikke støtter 'OneDrive Consumer' versjonen av onedrive.
 *
 * Se https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers
 *
 * Inspirert av https://github.com/shivamarora1/onedrive-angular/, se her for bruk mot OneDrive Consumer.
 * Koden i sdk-pnptimeline er kopiert fra https://github.com/OneDrive/samples/tree/master/samples/file-picking/sdk-pnptimeline
 *
 * Se lederplan/dokumentasjon/integrasjoner/azure-onedrive.md for ytterligere info om integrasjonen.
 * </pre>
 */
@Injectable()
export class OneDriveFilvelgerGui {

    static readonly protokollOgDomeneRegexp = /(https:\/\/.*?)\/.*/i;
    private microsoftLogin: Promise<MicrosoftLogin>;
    private picker: Picker | null = null;

    constructor(private avhengigheter: OneDriveFilvelgerGuiAvhengigheter, private kontekst: BrukerkontekstTilbyder, private oversettelser: Oversettelser) {
        this.microsoftLogin = avhengigheter.oneDrivePickerKonfigurasjon().then(konfigurasjon => new MicrosoftLogin(konfigurasjon.clientId, konfigurasjon.redirectUrl));
        this.kontekst.brukerLoggetUtSomStrøm().subscribe(() => this.deinitialiaser());
    }

    /** Viser onedrive picker gui, returnerer valgte filer, eller null/undefined dersom ingen filer ble valgt */
    async visPickerGUI(vindu: PickerWindowTilbyder): Promise<ValgteSkyfiler<OneDriveFil> | null> {
        try {
            const sharepointUrl = await this.sharepointUrl();
            if (erTomEllerNil(sharepointUrl)) {
                this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('Kunne ikke finne webadressen til din OneDrive')));
                return null;
            }
            this.avhengigheter.infomelding(this.oversettelser.oversett(i18n('Forbindelse til OneDrive opprettet')));
            return this.visFilePickerDialog(sharepointUrl, vindu);

        } catch (e: any) {
            debug('visPickerGUI, feil', e)

            //kan ikke benytte MicrosoftLogin.erUserCanceledError(e) da feilen er pakket inn i en GraphError
            if (e instanceof GraphError && e.body.includes('user_cancelled')) {
                //bruker trykket på avbryt i oauth innloggingsdialogen
                return null;

            }
            //kan ikke benytte MicrosoftLogin.erConsentRequiredError(e) da feilen er pakket inn i en GraphError
            else if (e instanceof GraphError && e.body.includes('consent_required')) {
                //bruker trykket på avbryt i oauth innloggingsdialogen
                return null;

            } else if (e instanceof GraphError && e.body.includes('Unable to find target address')) {
                //bruker logget inn med konto som ikke er tilknyttet onedrive bussiness (sharepoint)
                this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('Valgt Microsoft kontoen er ikke knyttet til OneDrive for Business')));
                await this.logut();

            } else if (e.message?.includes('popup_window_error')) {
                //popupblokker hindret åpning av innloggingsdialogen
                this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('Feil ved åpning av et popup-vindu. Du må tillate åpning av popup-vinduer i nettleseren din')));

            } else {
                //generell feil
                this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('Kunne ikke opprettet forbindelse til OneDrive, vennligst prøv på nytt') + ` (${e})`));
            }

            return null;
        }
    }

    async logut() {
        debug('logut')
        this.picker?.abort(); //fører til at picker.activate returnerer med null

        try {
            await (await this.microsoftLogin).logut();

        } catch (e: any) {
            debug('logut, feil', e)
            if (e.message?.includes('popup_window_error')) {
                //popupblokker hindret åpning av innloggingsdialogen
                this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('Feil ved åpning av et popup-vindu. Du må tillate åpning av popup-vinduer i nettleseren din')));
            } else {
                //generell feil
                this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('Kunne ikke logge av aktiv Microsoft konto, vennligst prøv på nytt'), e));
            }
        }
    }

    //private hjelpemetoder

    private async deinitialiaser() {
        this.picker = null;
    }

    private async sharepointUrl() {
        // Bruker .default scopet slik at vi spør om tilgangene definert i OneDriveFilePicker i styreplan-infrastruktur prosjektet.
        // Strengt tatt kreves kun Files.Read.All (se https://learn.microsoft.com/en-us/graph/api/site-get?view=graph-rest-1.0&tabs=http#permissions)
        // men siden dette er det første autentiseringskallet vil dette kallet også definere hvilke consents bruker blir
        // spurt om - derfor må alle nødvendige tilganger for å realisere FilePicker funk. inkluderes her.
        const tilganger = ['https://graph.microsoft.com/.default'];
        const klient = Client.init({authProvider: (await this.microsoftLogin).authProvider(tilganger)});
        const resultat: MicrosoftGraph.Site = await klient.api('/sites/root').get();
        debug('sharepointUrl: graph kall returnert', resultat)
        return resultat.webUrl;
    }

    private async visFilePickerDialog(baseUrl: string, vindu: PickerWindowTilbyder): Promise<ValgteSkyfiler<OneDriveFil> | null> {
        debug('visFilePickerDialog: åpner', baseUrl)
        const pickerVindu = await vindu();
        this.picker = new Picker(pickerVindu).using(
          ResolveWithPicks(),
          Popup(),
          MSALAuthenticate(await this.microsoftLogin),
        );
        this.picker.on.error(err => this.vedPickerFeil(err));
        this.picker.on.notification(message => this.vedPickerNotification(message));
        this.picker.on.log(((message, level) => this.vedPickerLog(message, level)));
        this.picker.on.close(() => this.vedPickerClose());

        const resultat = await this.picker.activate({baseUrl: baseUrl, options: sharepointFilePickerOptions});
        debug('visFilePickerDialog: picker activate returnerte', resultat)

        return resultat ? this.håndterDokumenterValgt(resultat.items) : null;
    }

    //dersom picker skal vises i en popup
    /*private pickerVindu(): Window | null {
        const vindu = window.open("", "Picker", "width=800,height=600");
        if (erNil(vindu)) {
            this.meldingTilBruker.feilmelding(this.oversettelser.oversett(i18n('Du må tillate åpning av popup-vinduer')));
            return null;
        }
        //bruker kan lukke vindu (popup) ved å klikke på 'avbryt'-knappen - hvorpå picker.activate returnerer med null
        //dersom bruker lukker vindu på annet vis detekterer vi det med koden under
        let detekterVinduLukket = setInterval(() => {
            if (vindu.closed) {
                debug('pickerVindu: Vinduet ble lukket');
                clearInterval(detekterVinduLukket);
                this.picker.abort(); //fører til at picker.activate returnerer med null
            }
        }, 1000); // Sjekk hvert sekund (1000 millisekunder)
        return vindu;
    }*/

    private async håndterDokumenterValgt(filer: SPItem[]): Promise<ValgteSkyfiler<OneDriveFil>> {
        debug('håndterDokumenterValgt', filer)
        const accessToken = await (await this.microsoftLogin).hentEksisterendeToken({scopes: [this.hentTokenScope(filer[0])]});

        return {
            aksessToken: accessToken,
            dokumenter: filer.map(fil => {
                return {
                    fileId: fil.id,
                    driveId: fil.parentReference.driveId,
                    sharePointEndpoint: fil['@sharePoint.endpoint'],
                    filnavn: fil.name,
                    url: fil.webUrl,
                };
            })
        };
    }

    private hentTokenScope(file: SPItem) {
        const endpoint = file['@sharePoint.endpoint'];
        const protokollOgDomene = OneDriveFilvelgerGui.protokollOgDomeneRegexp.exec(endpoint)![1]
        return `${protokollOgDomene}/.default`;
    }

    private vedPickerFeil(err: any) {
        debug('vedPickerFeil', err);
        this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('En feil oppstod ved valg av filer fra OneDrive')) + ` (${err})`);
    }

    private vedPickerNotification(message: any) {
        debug("picker.on.notification: " + JSON.stringify(message));
    }

    private vedPickerLog(message: any, level: number) {
        debug(`picker.on.log: [${level}] ${message}`);
    }

    private vedPickerClose() {
        debug('picker.on.close')
    }
}

@Injectable()
export class OneDriveFilvelgerGuiAvhengigheter {
    constructor(private sakSkyfilOpplastingRessurs: SakSkyfilOpplastingRessursClient, private meldingTilBruker: MeldingTilBruker) {
    }

    oneDrivePickerKonfigurasjon(): Promise<MicrosoftOneDrivePickerKonfigurasjonModell> {
        return this.sakSkyfilOpplastingRessurs.microsoftOneDrivePickerKonfigurasjon().somPromise();
    }

    feilmelding(melding: string) {
        this.meldingTilBruker.feilmelding(melding);
    }

    infomelding(melding: string) {
        this.meldingTilBruker.infomelding(melding);
    }
}

// se https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/v8-schema?view=odsp-graph-online
const sharepointFilePickerOptions: IFilePickerOptions = {
    sdk: '8.0',
    //Configuration for the entry location to which the File Picker will navigate on load.
    entry: {
        oneDrive: {} //fører til at 'mine filer' valget i venstremenyen er aktivert som standard
    },
    // Applications must pass this empty `authentication` option in order to obtain details item data
    // from the picker, or when embedding the picker in an iframe.
    authentication: {},
    messaging: {
        origin: window.location.origin,
        channelId: nyUuid()
    },
    typesAndSources: {
        filters: tillatteFiltyper.flatMap(type => type.filendelse).map(endelse => `.${endelse}` as ExtFilter),
        mode: 'files',
        //pivots styrer hvilke menyinnslag som vises i venstremenyen i filepicker (lokasjoner),
        //pivots: {}, //alle pivots defaulter til true
    },
    selection: {
        mode: 'multiple',
        maximumCount: 10,
    },
    //kommenter inn denne for at pick-respons skal inneholde feltet '@content.downloadUrl' med en url som kan benyttes til å laste ned fila
    /*commands: {
        pick: {
            select: {
                urls: {
                    download: true
                }
            }
        }
    }*/
};

export type PickerWindowTilbyder = () => Promise<Window>;

export interface OneDriveFil extends Skyfil {
    fileId: string
    driveId: string;
    sharePointEndpoint: string;
    url: string;
}

/*
Eksempel på fil data returnert fra picker (SPItem)
{
 "command": "pick",
 "items": [
   {
     "name": "Dokument.docx",
     "webDavUrl": "https://sysfab-my.sharepoint.com/personal/kai_sysfab_onmicrosoft_com/Documents/Dokument.docx",
     "webUrl": "https://sysfab-my.sharepoint.com/personal/kai_sysfab_onmicrosoft_com/_layouts/15/Doc.aspx?sourcedoc=%7B7345715A-F804-4FD6-A966-940E965D3632%7D&file=Dokument.docx&action=default&mobileredirect=true",
     "size": 1359658,
     "id": "01UNDIODC2OFCXGBHY2ZH2SZUUB2LF2NRS",
     "parentReference": {
       "driveId": "b!JmuG_xBuo063ns8E_V9d80BLwzJ5AHhCi9E_FDqNeVxgOya1X3DXSYKcZXQO8-Ig",
       "sharepointIds": {
         "listId": "b5263b60-705f-49d7-829c-65740ef3e220",
         "webId": "32c34b40-0079-4278-8bd1-3f143a8d795c",
         "siteId": "ff866b26-6e10-4ea3-b79e-cf04fd5f5df3",
         "siteUrl": "https://sysfab-my.sharepoint.com/personal/kai_sysfab_onmicrosoft_com"
       }
     },
     "sharepointIds": {
       "listItemUniqueId": "7345715a-f804-4fd6-a966-940e965d3632",
       "listItemId": "2",
       "listId": "b5263b60-705f-49d7-829c-65740ef3e220",
       "webId": "32c34b40-0079-4278-8bd1-3f143a8d795c",
       "siteId": "ff866b26-6e10-4ea3-b79e-cf04fd5f5df3",
       "siteUrl": "https://sysfab-my.sharepoint.com/personal/kai_sysfab_onmicrosoft_com"
     },
     "@sharePoint.embedUrl": "https://sysfab-my.sharepoint.com/personal/kai_sysfab_onmicrosoft_com/_layouts/15/Embed.aspx?UniqueId=7345715a%2Df804%2D4fd6%2Da966%2D940e965d3632",
     "@sharePoint.endpoint": "https://sysfab-my.sharepoint.com/_api/v2.0",
     "@sharePoint.listUrl": "https://sysfab-my.sharepoint.com/personal/kai_sysfab_onmicrosoft_com/Documents"
   }
 ],
 "keepSharing": false
}
*/
